import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  addPageMetaValidationSchema,
  TAddPageMetaValidation,
  useAddPageMetaMutation,
} from '../../../api/add-page-meta.api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { GET_LIST_INTEGRATION_META_QUERY_KEY } from '../../../api/get-list-integration-meta.api';
import { queryClient } from '@/lib/react-query';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import Input from '@/components/ui/input';
import { Plus, X } from 'lucide-react';
import { Spinner } from '@/components/_ui/spinner';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';

export type TAddPageMetaModalType = 'AddPageMetaModal';
export interface IAddPageMetaModalProps {
  state?: string;
}

const TOAST_MESSAGES_ADD_PAGE_META = {
  success: 'Meta page added successfully',
  error: 'Failed to add Meta page',
};

const AddPageMetaModal = memo(({ state }: IAddPageMetaModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const navigate = useNavigate();
  const channelModalRootActions = useChannelModalRootActionsStore();
  const isOpenAddPageMetaModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('AddPageMetaModal');
  }, [channelModalRootActions]);
  const [currentPageId, setCurrentPageId] = useState('');
  const form = useForm<TAddPageMetaValidation>({
    mode: 'onChange',
    resolver: zodResolver(addPageMetaValidationSchema),
  });

  const mutateAddPageMeta = useAddPageMetaMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_ADD_PAGE_META.error;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_INTEGRATION_META_QUERY_KEY],
          }),
        ]);
        channelModalRootActions.closeModal('AddPageMetaModal');
        navigate('/channel-integrations/social-media?tabActive=meta');
        showToast({
          message: TOAST_MESSAGES_ADD_PAGE_META.success,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isDisabledButton = useMemo(() => {
    return mutateAddPageMeta.isPending || form.watch('ids')?.length === 0;
  }, [mutateAddPageMeta.isPending, form.watch('ids')]);

  // EFFECTS
  useEffect(() => {
    if (state) {
      form.reset({
        state: state,
        ids: [],
      });
    }
  }, [state, form]);

  // HANDLERS
  const onSubmit = useCallback(() => {
    const state = form.watch('state');
    const ids = form.watch('ids');
    if (!state || ids?.length === 0) {
      showToast({
        type: 'error',
        message: 'Please select a state and add at least one page ID!',
      });
      return;
    }

    mutateAddPageMeta.mutate({
      state,
      ids,
    });
  }, [mutateAddPageMeta, showToast]);

  const handleAddPageId = useCallback(() => {
    if (!currentPageId.trim()) {
      showToast({
        type: 'error',
        message: 'Please enter a page ID',
      });
      return;
    }

    const currentNames = form.watch('ids') || [];
    if (currentNames.includes(currentPageId.trim())) {
      showToast({
        type: 'error',
        message: 'This page ID already exists',
      });
      return;
    }

    const newNames = [...currentNames, currentPageId.trim()];
    form.setValue('ids', newNames);
    setCurrentPageId('');
  }, [currentPageId, form, showToast]);

  const handleRemovePageId = useCallback(
    (pageIdToRemove: string) => {
      const currentNames = form.watch('ids') || [];
      const newNames = currentNames.filter((pageId) => pageId !== pageIdToRemove);
      form.setValue('ids', newNames);
    },
    [form]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleAddPageId();
      }
    },
    [handleAddPageId]
  );

  const handleCloseModal = useCallback(() => {
    channelModalRootActions.closeModal('AddPageMetaModal');
  }, [channelModalRootActions]);

  const handleRemoveAllPageIds = useCallback(() => {
    form.setValue('ids', []);
  }, [form]);

  return (
    <Modal
      isOpen={isOpenAddPageMetaModal}
      lockBodyScroll
      onClose={handleCloseModal}
      isCloseOutside={false}
      className="xl:w-[500px] w-[90vw] p-[32px]"
    >
      <ModalHeader>
        <ModalTitle>Add Page Meta</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form id="add-page-meta-form" className="gap-[12px] h-[300px] flex flex-col min-h-0">
          <div className="flex flex-col gap-2">
            <label className="text-[14px] font-normal text-[#25151E]">Page ID</label>
            <div className="flex gap-2">
              <Input
                id="page-id"
                placeholder="Page ID (e.g., 123456789012345)"
                value={currentPageId}
                onChange={(e) => setCurrentPageId(e.target.value)}
                onKeyDown={handleKeyPress}
                className="flex-1"
              />
              <button
                type="button"
                onClick={handleAddPageId}
                className="px-4 py-2 bg-[#0C8DBF] text-white rounded-[8px] text-[14px] font-normal hover:bg-[#0a7ba8] flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            {form.formState.errors.ids && (
              <div className="text-[12px] font-normal text-[#CC0202]">
                <span className="label-text-alt text-error">{form.formState.errors.ids?.message}</span>
              </div>
            )}
          </div>

          {/* Page IDs List */}
          <div className="flex-1 flex flex-col min-h-0 gap-[4px]">
            {form.watch('ids')?.length > 0 && (
              <div className="flex items-center justify-between mb-[4px]">
                <span className="text-[14px] font-medium text-[#25151E]">
                  Added Pages ({form.watch('ids')?.length || 0})
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveAllPageIds()}
                  className="text-[#CC0202] flex items-center gap-1 hover:text-[#a00000] px-[5px] py-[3px] bg-[#fef2f2] text-[12px] font-normal rounded transition-colors"
                  title="Remove all page IDs"
                >
                  Remove All <X className="w-4 h-4 flex-shrink-0" />
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto border border-[#D9D9D9] rounded-[8px] min-h-0 p-2">
              {form.watch('ids')?.length === 0 ? (
                <div className="text-center text-[#9D9D9C] text-[14px] py-4">No page IDs added yet</div>
              ) : (
                <div className="space-y-2">
                  {form.watch('ids')?.map((pageId, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-[#F8F9FA] rounded-[6px] px-3 py-2 border border-[#E9ECEF]"
                    >
                      <span className="text-[14px] font-normal text-[#25151E] flex-1">{pageId}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePageId(pageId)}
                        className="text-[#CC0202] hover:text-[#a00000] p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton disabled={isDisabledButton} onClick={() => onSubmit()}>
          {mutateAddPageMeta.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Connect
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

AddPageMetaModal.displayName = 'AddPageMetaModal';
export default AddPageMetaModal;
